import { useState, useEffect } from 'react';

type ConsentState = 'pending' | 'accepted' | 'rejected';

const STORAGE_KEY = 'orsonet_cookie_consent';

export default function CookieConsent() {
  const [state, setState] = useState<ConsentState>('pending');
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ConsentState | null;
    if (saved === 'accepted' || saved === 'rejected') {
      setState(saved);
      setVisible(false);
    } else {
      // Small delay so the banner slides in after page loads
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setState('accepted');
    setVisible(false);
  };

  const reject = () => {
    localStorage.setItem(STORAGE_KEY, 'rejected');
    setState('rejected');
    setVisible(false);
  };

  if (state !== 'pending') return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] transition-all duration-500"
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(110%)',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {/* Backdrop blur strip */}
      <div className="mx-auto max-w-[1200px] m-3 md:m-4 rounded-2xl overflow-hidden"
        style={{ background: 'rgba(10,10,10,0.95)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)' }}>

        <div className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-5 md:gap-8">

            {/* Icon + text */}
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5"
                style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.2)' }}>
                <i className="ri-shield-check-line text-[#00d4ff] text-lg" />
              </div>
              <div className="min-w-0">
                <h3 className="text-white font-bold text-sm md:text-base mb-1">Używamy plików cookie</h3>
                <p className="text-white/50 text-xs md:text-sm leading-relaxed">
                  Ta strona używa plików cookie zgodnie z{' '}
                  <a href="/polityka-prywatnosci" className="text-[#00d4ff] hover:text-[#00ff88] underline transition-colors cursor-pointer">
                    Polityką Prywatności
                  </a>
                  {' '}i Rozporządzeniem RODO (UE 2016/679). Pliki niezbędne działają zawsze;
                  analityczne i marketingowe wymagają Twojej zgody.
                </p>

                {/* Details toggle */}
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="mt-2 text-[11px] text-white/35 hover:text-white/65 transition-colors cursor-pointer flex items-center gap-1"
                >
                  <i className={`ri-arrow-${showDetails ? 'up' : 'down'}-s-line`} />
                  {showDetails ? 'Ukryj szczegóły' : 'Szczegóły kategorii'}
                </button>

                {/* Expanded details */}
                {showDetails && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {[
                      {
                        icon: 'ri-settings-3-line',
                        color: '#00ff88',
                        name: 'Niezbędne',
                        desc: 'Sesja, bezpieczeństwo, logowanie — zawsze aktywne.',
                        required: true,
                      },
                      {
                        icon: 'ri-bar-chart-2-line',
                        color: '#00d4ff',
                        name: 'Analityczne',
                        desc: 'Google Analytics — anonimowe statystyki ruchu.',
                        required: false,
                      },
                      {
                        icon: 'ri-megaphone-line',
                        color: '#ff6b9d',
                        name: 'Marketingowe',
                        desc: 'Google Ads, Meta Pixel — dopasowane reklamy.',
                        required: false,
                      },
                    ].map((cat) => (
                      <div key={cat.name}
                        className="rounded-xl p-3"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className={`${cat.icon} text-xs`} style={{ color: cat.color }} />
                          </div>
                          <span className="text-white text-xs font-semibold">{cat.name}</span>
                          {cat.required && (
                            <span className="ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                              style={{ background: 'rgba(0,255,136,0.12)', color: '#00ff88' }}>
                              Wymagane
                            </span>
                          )}
                        </div>
                        <p className="text-white/40 text-[10px] leading-relaxed">{cat.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 flex-shrink-0 md:min-w-[180px]">
              <button
                onClick={accept}
                className="w-full px-6 py-3 rounded-xl font-bold text-sm text-black whitespace-nowrap cursor-pointer transition-all duration-200 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #00d4ff 0%, #00ff88 100%)' }}
              >
                Akceptuj wszystkie
              </button>
              <button
                onClick={reject}
                className="w-full px-6 py-3 rounded-xl font-semibold text-sm text-white/60 whitespace-nowrap cursor-pointer hover:text-white transition-all border border-white/10 hover:border-white/25"
              >
                Tylko niezbędne
              </button>
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="px-5 md:px-6 py-2.5 border-t border-white/5 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-white/25 text-[10px]">© {new Date().getFullYear()} ORSONET. Wszelkie prawa zastrzeżone.</span>
          <a href="/polityka-prywatnosci" className="text-white/30 text-[10px] hover:text-white/60 transition-colors cursor-pointer">Polityka prywatności</a>
          <a href="/polityka-cookies" className="text-white/30 text-[10px] hover:text-white/60 transition-colors cursor-pointer">Polityka cookies</a>
          <span className="text-white/15 text-[10px] ml-auto hidden md:block">RODO · UE 2016/679</span>
        </div>
      </div>
    </div>
  );
}
